import * as styles from '../FirstResponder.module.css';
import { LiveAttention } from '../LivePreview/liveViewModel';

/*
 * Canonical presentation component (GRAVY-10373 convergence, Phase 1 extraction).
 * Extracted verbatim from LiveConsole.tsx's Needs Attention card. Renders nothing when there is
 * nothing to say (attention is null or count is 0) — an empty attention card trains people to
 * ignore the real one.
 *
 * GRAVY-10373 I2: `count` is the wire's true unresolved_count total, which can exceed
 * `items.length` (the one fetched page, 25 rows). When it does, a "Showing N of M" note renders
 * under the list so the headline number and the visible rows never silently disagree.
 *
 * F11: each row names the WORKER it is about — "Worker #501 · Push delivery failed". Every
 * attention class describes an invitation that could not reach one specific person, so the reason
 * alone left the operator with a job id and no one to act on, and two failures on the same job
 * were indistinguishable. The worker label is composed into `summary` by the adapter
 * (Live/liveWireAdapter.ts), which is also where the internal `Worker #<id>` transformation lives
 * — no real name reaches this component.
 */

type Props = {
  attention: LiveAttention;
  onSelectJob: (id: string) => void;
};

const NeedsAttentionCard = ({ attention, onSelectJob }: Props) => {
  if (!attention || attention.count === 0) return null;
  return (
    <div
      className={`${styles.card} ${styles.attention}`}
      data-testid="flp-attention"
    >
      <div className={styles.section}>
        <div className={styles.strong}>
          {/* The count is of invitations (one row per worker per job), not jobs. */}
          {attention.count} invitation{attention.count === 1 ? '' : 's'} need
          {attention.count === 1 ? 's' : ''} attention
        </div>
        {attention.items.map((item) => (
          <div
            // F11: keyed by the whole line, not by jobId alone — one job can raise an attention
            // row per worker, and those rows are now genuinely different rows.
            key={`${item.jobId}·${item.summary}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
              marginTop: 8,
            }}
            data-testid={`flp-attention-item-${item.jobId}`}
          >
            <span className={`${styles.muted} ${styles.small}`}>
              {item.summary}
            </span>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
              data-testid={`flp-attention-view-${item.jobId}`}
              onClick={() => onSelectJob(item.jobId)}
            >
              View
            </button>
          </div>
        ))}
        {attention.items.length < attention.count && (
          <div
            className={`${styles.muted} ${styles.small}`}
            style={{ marginTop: 8 }}
            data-testid="flp-attention-page-note"
          >
            Showing {attention.items.length} of {attention.count}
          </div>
        )}
      </div>
    </div>
  );
};

export default NeedsAttentionCard;
